import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import {
  LayoutDashboard, FileText, Users, Tag, CreditCard, Calendar,
  IdCard, BarChart3, Shield, Newspaper, BookOpen, LogOut, ChevronLeft, Mail, Phone
} from 'lucide-react';

const ALL_MENU_ITEMS = [
  { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'publisher'] },
  { path: '/admin/artigos', icon: FileText, label: 'Publicações', roles: ['admin', 'publisher'] },
  { path: '/admin/artigos/revisao', icon: BookOpen, label: 'Revisão', roles: ['admin', 'publisher'] },
  { path: '/admin/categorias', icon: Tag, label: 'Criar Categoria', roles: ['admin', 'publisher'] },
  { path: '/admin/newsletter', icon: Mail, label: 'Newsletter', roles: ['admin', 'publisher'] },
  { path: '/admin/sms', icon: Phone, label: 'SMS', roles: ['admin'] },
  { path: '/admin/agendamentos', icon: Calendar, label: 'Agendamentos', roles: ['admin', 'consul', 'consular_agent'] },
  { path: '/admin/CardC', icon: IdCard, label: 'CConsular', roles: ['admin', 'consul', 'consular_agent'] },
  { path: '/admin/SCondut', icon: FileText, label: 'SCondut', roles: ['admin', 'consul', 'consular_agent', 'auditor'] },
  { path: '/admin/aconsular', icon: Shield, label: 'S. Consular', roles: ['admin', 'consul', 'consular_agent'] },
  { path: '/admin/consul', icon: Shield, label: 'Consul', roles: ['admin', 'consul'] },
  { path: '/admin/pagamentos', icon: CreditCard, label: 'Pagamentos', roles: ['admin', 'financial'] },
  { path: '/admin/financeiro', icon: BarChart3, label: 'Financeiro', roles: ['admin', 'financial'] },
  { path: '/admin/utilizadores', icon: Users, label: 'Utilizadores', roles: ['admin'] },
];

const ROLE_LABELS = {
  admin: 'Administrador',
  publisher: 'Publicador',
  consul: 'Cônsul',
  consular_agent: 'Agente Consular',
  financial: 'Financeiro',
  auditor: 'Auditor',
  editor: 'Editor',
};

export default function BackofficeLayout() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const role = user?.role || 'user';
  const menuItems = ALL_MENU_ITEMS.filter(item => item.roles.includes(role));

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Redirect to first allowed page if landing on /admin
  const firstAllowed = menuItems[0]?.path || '/Users';

  return (
    <div className="flex min-h-screen bg-gray-100" data-testid="backoffice-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0A0A0A] text-white flex flex-col flex-shrink-0">
        <div className="p-4 border-b border-gray-800">
          <Link to="/Users" className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-3 transition-colors" data-testid="back-to-profile">
            <ChevronLeft size={16} />
            Voltar ao Perfil
          </Link>
          <h2 className="text-lg font-bold">Backoffice</h2>
          <div className="flex items-center gap-2 mt-2">
            <div className="w-8 h-8 rounded-full bg-[#CC0000] flex items-center justify-center text-sm font-bold">
              {user?.full_name?.[0] || 'U'}
            </div>
            <div>
              <p className="text-sm font-medium">{user?.full_name?.split(' ')[0]}</p>
              <p className="text-[10px] uppercase tracking-wider text-[#FFD700]">{ROLE_LABELS[role] || role}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {menuItems.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.path === '/admin/artigos' && location.pathname.startsWith('/admin/artigos') && !location.pathname.includes('revisao'));
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`sidebar-${item.path.split('/').pop()}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-[#CC0000] text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
              >
                <Icon size={18} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-gray-800">
          <button
            onClick={handleLogout}
            data-testid="sidebar-logout"
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-gray-400 hover:text-red-400 hover:bg-gray-800 transition-colors"
          >
            <LogOut size={18} />
            Sair
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
